/* eslint-disable */
import React, { useState, useEffect, useCallback } from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericSelect from "@ui-components/Select/GenericSelect";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericDateTimeRangePicker from "@ui-components/DateTime/GenericDateTimeRangePicker";
import { createLicenseV1, updateLicenseV1, fetchCustomersWithTenant, fetchPartnersWithTenant, fetchSubscriptionsForTenant } from "./request/request";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppSelector, useAppDispatch } from "@redux/hooks";
import { setSubscriptionId } from "@redux/slices/tenantSlice";

interface LicensePrefill {
  id?: number;
  tenantId?: number;
  serial?: string;
  description?: string;
  type?: string;
  startdate?: string;
  enddate?: string;
  total?: number;
  activated?: number;
}

interface AddLicenseModalV1Props {
  isOpen: boolean;
  onClose: () => void;
  onCreate: () => void;
  prefill?: LicensePrefill | null;
}

const AddLicenseModalV1: React.FC<AddLicenseModalV1Props> = ({
  isOpen,
  onClose,
  onCreate,
  prefill,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const dispatch = useAppDispatch();
  const userProfile = useAppSelector((state) => state.userProfile);
  const isGlobalAdmin = userProfile.TenantType === "GLOBAL_ADMIN";
  const { tenantId: currentTenantId } = useAppSelector(state => state.selection);
  const [tenant, setTenant] = useState("");
  const [description, setDescription] = useState("");
  const [licenseType, setLicenseType] = useState("CTXR-TC-AWS-S-1Y");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [numDevices, setNumDevices] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entities, setEntities] = useState<{ value: string; label: string }[]>([]);
  const [isLoadingEntities, setIsLoadingEntities] = useState(false);
  const [selectedCustomerSubscriptionId, setSelectedCustomerSubscriptionId] = useState<string | null>(null);
  const [isLoadingSubscriptionId, setIsLoadingSubscriptionId] = useState(false);
  
  // Check if we're in edit mode (prefill has an id)
  const isEditMode = prefill?.id !== undefined;
  
  // License types for v1
  const licenseTypes = [
    { value: "CTXR-TC-AWS-S-3Y", label: "CTXR-TC-AWS-S-3Y" },
    { value: "CTXR-TC-AWS-M-3Y", label: "CTXR-TC-AWS-M-3Y" },
    { value: "CTXR-TC-AWS-L-3Y", label: "CTXR-TC-AWS-L-3Y" },
    { value: "CTXR-TC-AWS-S-2Y", label: "CTXR-TC-AWS-S-2Y" },
    { value: "CTXR-TC-AWS-M-2Y", label: "CTXR-TC-AWS-M-2Y" },
    { value: "CTXR-TC-AWS-L-2Y", label: "CTXR-TC-AWS-L-2Y" },
    { value: "CTXR-TC-AWS-S-1Y", label: "CTXR-TC-AWS-S-1Y" },
    { value: "CTXR-TC-AWS-M-1Y", label: "CTXR-TC-AWS-M-1Y" },
    { value: "CTXR-TC-AWS-L-1Y", label: "CTXR-TC-AWS-L-1Y" },
  ];

  // Helper function to parse date from "dd-MM-yyyy" format or ISO string
  const parseDate = (dateString: string | undefined): Date | null => {
    if (!dateString) return null;
    
    // Try parsing as ISO string first
    const isoDate = new Date(dateString);
    if (!isNaN(isoDate.getTime())) {
      return isoDate;
    }
    
    // Try parsing as "dd-MM-yyyy" format
    const parts = dateString.match(/(\d{2})-(\d{2})-(\d{4})/);
    if (parts) {
      const day = parseInt(parts[1], 10);
      const month = parseInt(parts[2], 10) - 1; // Month is 0-indexed
      const year = parseInt(parts[3], 10);
      const date = new Date(year, month, day);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
    
    return null;
  };

  // Helper function to format date as YYYY-MM-DD
  const formatDateToYYYYMMDD = (date: Date | null): string => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Clear error when modal opens or closes
  useEffect(() => {
    if (isOpen) {
      setError(null);
      if (prefill) {
        // Prefill form with existing license data
        if (prefill.description) setDescription(prefill.description);
        if (prefill.type) setLicenseType(prefill.type);
        if (prefill.startdate) {
          const parsedStartDate = parseDate(prefill.startdate);
          setStartDate(parsedStartDate);
        }
        if (prefill.enddate) {
          const parsedEndDate = parseDate(prefill.enddate);
          setEndDate(parsedEndDate);
        }
        if (prefill.total !== undefined) setNumDevices(String(prefill.total));
        if (prefill.tenantId) setTenant(String(prefill.tenantId));
      } else {
        // Reset form when opening for new license
        setDescription("");
        setLicenseType("CTXR-TC-AWS-S-1Y");
        setStartDate(null);
        setEndDate(null);
        setNumDevices("");
        setTenant("");
        setSelectedCustomerSubscriptionId(null);
      }
    }
  }, [isOpen, prefill]);

  // Fetch subscription ID for the selected customer/tenant
  // Uses the same endpoint as HA clusters: /v1/suscription?tenantId=...
  const fetchCustomerSubscriptionId = useCallback(async (customerTenantId: number) => {
    if (!customerTenantId) {
      setSelectedCustomerSubscriptionId(null);
      return;
    }

    setIsLoadingSubscriptionId(true);
    setError(null); // Clear previous errors
    try {
      const token = await getAccessTokenSilently();
      const response = await fetchSubscriptionsForTenant(token, customerTenantId);
      
      // Response should be an array of subscriptions: [{ id: number, purchaseOrden: string }, ...]
      const subscriptions = response.data || [];
      
      if (!Array.isArray(subscriptions) || subscriptions.length === 0) {
        setSelectedCustomerSubscriptionId(null);
        setError("No subscriptions found for the selected customer. The customer needs to have a subscription before creating a license.");
        return;
      }
      
      // Use the first subscription's ID (same pattern as HA clusters)
      // Convert to string as the API expects string | number
      const firstSubscription = subscriptions[0];
      const subscriptionId = String(firstSubscription.id);
      
      if (!subscriptionId || subscriptionId === "undefined" || subscriptionId === "null") {
        setSelectedCustomerSubscriptionId(null);
        setError("Invalid subscription ID for the selected customer.");
      } else {
        setSelectedCustomerSubscriptionId(subscriptionId);
      }
    } catch (err: any) {
      console.error("Error fetching subscriptions for customer:", err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || "Unknown error";
      setSelectedCustomerSubscriptionId(null);
      setError(`Failed to fetch subscriptions: ${errorMessage}`);
    } finally {
      setIsLoadingSubscriptionId(false);
    }
  }, [getAccessTokenSilently]);

  const fetchEntitiesData = useCallback(async () => {
    setIsLoadingEntities(true);
    try {
      const token = await getAccessTokenSilently();
      let combined: { value: string; label: string }[] = [];
      // Always fetch customers
      const customersRes = await fetchCustomersWithTenant(token, currentTenantId);
      const customers = customersRes.data.data.map((customer: any) => ({
        value: customer.id.toString(),
        label: `Customer: ${customer.nameDisplay || customer.name}`
      }));
      combined = [...customers];
      if (isGlobalAdmin) {
        const partnersRes = await fetchPartnersWithTenant(token);
        const partners = partnersRes.data.data.map((partner: any) => ({
          value: partner.id.toString(),
          label: `Partner: ${partner.nameDisplay || partner.name}`
        }));
        combined = [...customers, ...partners];
      }
      setEntities(combined);
      // Auto-select first customer if none selected and not in edit mode
      if (combined.length > 0 && !tenant && !isEditMode) {
        const firstCustomerId = combined[0].value;
        setTenant(firstCustomerId);
        // Fetch subscription ID for the first customer
        const customerTenantId = parseInt(firstCustomerId);
        if (!isNaN(customerTenantId)) {
          fetchCustomerSubscriptionId(customerTenantId);
        }
      }
    } catch (err) {
      console.error('Error fetching entities:', err);
      setError('Failed to load tenants');
    } finally {
      setIsLoadingEntities(false);
    }
  }, [getAccessTokenSilently, currentTenantId, isGlobalAdmin, tenant, isEditMode, fetchCustomerSubscriptionId]);

  // Fetch customers/partners when modal opens
  useEffect(() => {
    if (isOpen && currentTenantId) {
      fetchEntitiesData();
    }
  }, [isOpen, currentTenantId, fetchEntitiesData]);

  // Fetch subscription ID when customer selection changes
  useEffect(() => {
    if (isOpen && tenant && !isEditMode) {
      const customerTenantId = parseInt(tenant);
      if (!isNaN(customerTenantId)) {
        fetchCustomerSubscriptionId(customerTenantId);
      }
    }
  }, [tenant, isOpen, isEditMode, fetchCustomerSubscriptionId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      // Get the access token
      const token = await getAccessTokenSilently();

      if (isEditMode && prefill?.id) {
        // Edit mode: Update existing license using v1 API
        const payload = {
          type: licenseType,
          total: parseInt(numDevices) || 0,
          description: description.trim(),
          enddate: endDate?.toISOString() || "",
          startdate: startDate?.toISOString() || "",
        };

        await updateLicenseV1(token, prefill.id, payload, currentTenantId);
      } else {
        // Create mode: Create new license using v1 API
        // Use subscription ID from the selected customer
        const subscriptionIdToUse = selectedCustomerSubscriptionId;
        
        if (!subscriptionIdToUse || subscriptionIdToUse === "-1" || subscriptionIdToUse === null) {
          setError("Subscription ID is required for the selected customer. Please wait for it to load or try again.");
          setIsLoading(false);
          return;
        }
        
        // Ensure subscriptionId is a string, not an object
        const subscriptionIdString = typeof subscriptionIdToUse === "string" 
          ? subscriptionIdToUse 
          : String(subscriptionIdToUse);
        
        if (subscriptionIdString === "-1" || subscriptionIdString === "null" || subscriptionIdString === "") {
          setError("Invalid subscription ID for the selected customer. Please try selecting a different customer.");
          setIsLoading(false);
          return;
        }
        
        const payload = {
          tenantId: parseInt(tenant),
          subscriptionId: subscriptionIdString,
          description: description.trim(),
          type: licenseType,
          startdate: formatDateToYYYYMMDD(startDate),
          enddate: formatDateToYYYYMMDD(endDate),
          total: numDevices || "0"
        };

        await createLicenseV1(token, payload, currentTenantId);
      }
      
      // Call the onCreate callback
      onCreate();
      
      // Close the modal on success
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || (isEditMode ? "Failed to update license" : "Failed to create license"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <GenericModal isOpen={isOpen} onClose={onClose} title={isEditMode ? "Edit License" : "Add License"} size="lg">
      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {error && (
          <div className="md:col-span-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}
        
        {!isEditMode ? (
          <GenericSelect
            label="Customer"
            options={entities}
            onSelect={(value) => {
              setError(null); // Clear any previous errors
              setTenant(value);
              setSelectedCustomerSubscriptionId(null); // Clear previous subscription ID
              // Fetch subscription ID for the selected customer
              const customerTenantId = parseInt(value);
              if (!isNaN(customerTenantId)) {
                fetchCustomerSubscriptionId(customerTenantId);
              }
            }}
            defaultValue={tenant}
            placeholder={isLoadingEntities ? "Loading tenants..." : "Select tenant"}
          />
        ) : (
          <div>
            <label className="block text-sm font-medium text-semantic-text-primary mb-1">Customer</label>
            <div className="px-3 py-2 bg-semantic-bg-secondary border border-semantic-border-secondary rounded text-semantic-text-secondary">
              {entities.find(e => e.value === tenant)?.label || tenant || "N/A"}
            </div>
          </div>
        )}
        {!isEditMode ? (
          <GenericSelect
            label="License Type"
            options={licenseTypes}
            onSelect={setLicenseType}
            defaultValue={licenseType}
            placeholder="Select license type"
          />
        ) : (
          <div>
            <label className="block text-sm font-medium text-semantic-text-primary mb-1">License Type</label>
            <div className="px-3 py-2 bg-semantic-bg-secondary border border-semantic-border-secondary rounded text-semantic-text-secondary">
              {licenseTypes.find(lt => lt.value === licenseType)?.label || licenseType || "N/A"}
            </div>
          </div>
        )}

        <GenericDateTimeRangePicker
          label="Start Date"
          value={{ start: startDate, end: null }}
          onChange={(val) => setStartDate(val.start)}
          singleDate
        />
        <GenericDateTimeRangePicker
          label="End Date"
          value={{ start: endDate, end: null }}
          onChange={(val) => setEndDate(val.start)}
          singleDate
        />
        <GenericInput
          label="Description"
          value={description}
          onChange={setDescription}
          placeholder="Description"
          required
        />
        
        <GenericInput
          label="Number of Devices"
          value={numDevices}
          onChange={setNumDevices}
          type="number"
          placeholder="Number of devices"
          required
        />
        {!isEditMode && isLoadingSubscriptionId && (
          <div className="md:col-span-2 text-sm text-semantic-text-secondary">
            Loading subscription ID for selected customer...
          </div>
        )}
        <div className="md:col-span-2 flex justify-end mt-4">
          <GenericButton
            label={isLoading ? (isEditMode ? "Updating..." : "Creating...") : (isEditMode ? "Update License" : "Create License")}
            type="submit"
            variant="brand"
            size="md"
            disabled={isLoading || isLoadingEntities || (!isEditMode && isLoadingSubscriptionId)}
            loading={isLoading}
          />
        </div>
      </form>
    </GenericModal>
  );
};

export default AddLicenseModalV1;
